import { type BaseQueryFn, type FetchArgs, type FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
// import { getTokenAndRefreshIfNeeded } from '@andyneville/garbanzo-lib/browser/authReducer'
import { getTokenAndRefreshIfNeeded } from './authReducer'
import type { AppDispatch, RootState } from '../store'
import type { IAccount, IMyAccountInput } from '../../../api/api'
import { rootLog } from '../logging'

const log = rootLog.child({ module: 'apiSlice' })

export const apiRootUrl = import.meta.env.VITE_API_ROOT_URL as string

export function unmarshallDates<T> (item: T, ...keys: string[]): T {
  for (const key of keys) {
    if (item != null && (item as Record<string, unknown>)[key] != null) {
      (item as Record<string, unknown>)[key] = new Date((item as Record<string, unknown>)[key] as string | number)
    }
  }
  return item
}

// https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
const baseQuery = fetchBaseQuery({
  baseUrl: apiRootUrl,
  prepareHeaders: async (headers, { getState }) => {
    const { auth: { accessToken } } = getState() as RootState
    if (accessToken != null && accessToken !== '') {
      headers.set('authorization', `Bearer ${accessToken}`)
    }
    return headers
  }
})

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  // log.debug('Update token if needed')
  const { getState, dispatch } = api
  // log.debug('baseQueryWithReauth: getTokenAndRefreshIfNeeded')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  log.debug('baseQueryWithReauth: getTokenAndRefreshIfNeeded', { args, api, extraOptions })
  await (getTokenAndRefreshIfNeeded())(dispatch as AppDispatch, getState as () => RootState)

  // log.debug('Base query')
  const result = await baseQuery(args, api, extraOptions)
  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Account', 'Tenant', 'AccountTenant', 'Season', 'Athlete', 'Tryout', 'Team', 'TeamAthlete', 'Recording', 'AdminAccount', 'AdminTenant', 'AdminTenantFull', 'AdminAccountTenant', 'AdminLogRequest', 'AdminLogRequestDetail', 'AdminSeason', 'AdminAthlete', 'AdminSeasonAthlete', 'AdminTryout', 'AdminTeam', 'AdminTeamAthlete'],
  endpoints: builder => ({
    getMyAccount: builder.query<IAccount, void>({ // eslint-disable-line @typescript-eslint/no-invalid-void-type
      query: () => '/accounts/me',
      providesTags: (result, _error, _arg) => [{ type: 'Account', id: result?.id }]
    }),
    editMyAccount: builder.mutation<IAccount, Partial<IMyAccountInput>>({
      query: (input: Partial<IMyAccountInput>) => ({
        url: '/accounts/me',
        method: 'PUT',
        body: input
      }),
      invalidatesTags: (result, _error, _arg) => [{ type: 'Account', id: result?.id }]
    }),
    submitFeedback: builder.mutation<void, { feedback: string }>({ // eslint-disable-line @typescript-eslint/no-invalid-void-type
      query: (input: { feedback: string }) => ({
        url: '/accounts/feedback',
        method: 'POST',
        body: input
      })
    })
  })
})

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  internalActions,
  useGetMyAccountQuery,
  useEditMyAccountMutation,
  useSubmitFeedbackMutation
} = apiSlice
